<template>
  <nav
    :aria-label="t('admin.setup.rail.ariaLabel')"
    class="flex flex-col gap-4 border-b border-sidebar-border bg-sidebar px-6 py-8 text-sidebar-foreground md:sticky md:top-0 md:h-dvh md:w-72 md:shrink-0 md:overflow-y-auto md:gap-8 md:border-r md:border-b-0"
  >
    <div class="flex items-center gap-3">
      <img v-if="logoRef" :src="logoRef" alt="" class="size-9! rounded-md object-contain!" />
      <span
        v-else
        class="flex size-9 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground"
        aria-hidden="true"
      >
        {{ initialRef }}
      </span>
      <div class="flex flex-col">
        <span class="text-sm font-semibold">{{ t('admin.setup.rail.title') }}</span>
        <span class="text-xs text-muted-foreground">{{ t('admin.setup.rail.subtitle') }}</span>
      </div>
    </div>

    <ol class="hidden flex-col gap-1 md:flex">
      <li v-for="(step, index) in SETUP_STEPS" :key="step">
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          :class="{ 'bg-sidebar-accent': step === current }"
          :aria-current="step === current ? 'step' : undefined"
          :data-test-id="`setup-rail-${step}`"
          @click="emit('select', step)"
        >
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold"
            :class="dotClass(step)"
          >
            <CheckIcon v-if="isDone(step)" class="size-3.5" :stroke-width="3" aria-hidden="true" />
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span class="flex min-w-0 flex-col gap-0.5">
            <span class="text-sm" :class="step === current ? 'font-semibold' : 'font-medium'">
              {{ t(`admin.setup.steps.${step}.label`) }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ isSkipped(step) ? t('admin.setup.rail.skipped') : t(`admin.setup.steps.${step}.hint`) }}
            </span>
          </span>
        </button>
      </li>
    </ol>

    <div class="flex flex-col gap-2 md:mt-auto">
      <span class="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex">
        <CheckIcon class="size-3.5" aria-hidden="true" />
        {{ t('admin.setup.rail.autosave') }}
      </span>
      <button
        type="button"
        class="w-fit text-sm font-medium text-primary underline-offset-4 hover:underline"
        data-test-id="setup-rail-later"
        @click="emit('later')"
      >
        {{ t('admin.setup.rail.later') }}
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { CheckIcon } from 'lucide-vue-next'
import { computed } from 'vue'

import type { SetupStepType } from '@/features/admin/platform-setup/constants'
import { SETUP_STEPS } from '@/features/admin/platform-setup/constants'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'

const props = defineProps<{
  current: SetupStepType
  completedSteps: SetupStepType[]
  visitedSteps: SetupStepType[]
}>()

const emit = defineEmits<{
  select: [step: SetupStepType]
  later: []
}>()

const { t } = useI18n()
const { whiteLabelRef } = useWhiteLabel()

const logoRef = computed(() => whiteLabelRef.value.logoSmall || undefined)
const initialRef = computed(() => (whiteLabelRef.value.companyName || 'C').trim().charAt(0).toUpperCase())

const isDone = (step: SetupStepType) => step !== props.current && props.completedSteps.includes(step)
// Une étape déjà vue mais incomplète a été passée.
const isSkipped = (step: SetupStepType) =>
  // L'équipe est facultative : la passer n'est pas un oubli.
  step !== 'recap' && step !== 'team' && step !== props.current && props.visitedSteps.includes(step) && !isDone(step)

function dotClass(step: SetupStepType) {
  if (step === props.current) return 'border-transparent bg-primary text-primary-foreground'
  if (isDone(step)) return 'border-transparent bg-success text-success-foreground'
  if (isSkipped(step)) return 'border-warning bg-card text-muted-foreground'
  return 'border-border bg-card text-muted-foreground'
}
</script>
